import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Repeat } from '@phosphor-icons/react';
import { PlayCircle } from '@phosphor-icons/react';
import classNames from 'classnames';
import { toast } from 'react-toastify';

import { sendDemoStepCompleteEmail } from '../../../UtilsEmail';

import {
  trackDemoGenerationFinish,
  trackDemoGenerationPlayCreationSuccess,
  trackDemoGenerationPlayCreationError,
  trackDemoGenerationClickContact,
  trackDemoGenerationDownloadSuccess,
  trackDemoGenerationDownloadError,
  trackDemoResetClick,
} from '../../../analytics';

import { Button, Spinner } from '../../../components/general';
import { ContentHeader } from '../../../components/layout/content';

import useDemoData from '../../../hooks/useDemoData';
import { useWindowWidth } from '../../../hooks/useWindowWidth';

import { getCreationWithData } from '../../../services/api/creations';
import { getTemplateData } from '../../../services/api/template';
import { sendSlackDemoNotification } from '../../../services/slackService';

import { PublicEvent, Person, HlReelShot, ModalError } from '../../../types';
import { getCreationVideoUrl, msecToSec, getBpWidth } from '../../../utils';

import PageSpinner from '../../General/pageSpinner';
import DemoLoading from '../components/demoLoading';
import RestartDemoModal from '../components/restartDemoModal';

const generatingTipsList = [
  'Stitching your moment into the highlight reel',
  'Filling the wildcard with your starring role',
  'Rendering pixels at light speed',
  'Mixing footage, timing, and a bit of CrowdClip&reg; magic',
  'Carefully placing your clip exactly where it belongs',
  'Building your personalised highlight reel frame by frame',
  'Aligning transitions like constellations',
  'Polishing the final cut just for you',
  'Turning raw footage into your spotlight moment',
  'Synchronising scenes and smoothing the edges',
  'Adding the final touches before liftoff',
  'Almost ready — exporting your personalised video',
];

const MOBILE_GAP = 32;
const VIDEO_WIDTH_SMALL = 256; // w-64
const VIDEO_WIDTH_LARGE = 320; // w-80

interface Props {
  event: PublicEvent;
  setError: React.Dispatch<React.SetStateAction<ModalError>>;
}

const GenerateStep: React.FC<Props> = (props) => {
  const { event, setError } = props;

  const { demoData, updateDemoData } = useDemoData();

  const windowWidth = useWindowWidth();
  const isMobileDevice = useMemo(
    () => windowWidth < getBpWidth('sm'),
    [windowWidth],
  );

  const [person, setPerson] = useState<Person | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [wildcardEnd, setWildcardEnd] = useState(10);
  const [isStarted, setIsStarted] = useState(false);

  const headingRef = useRef<HTMLDivElement | null>(null);
  const [headingHeight, setHeadingHeight] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoHeight, setVideoHeight] = useState(0);
  const [videoOffset, setVideoOffset] = useState(0);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const ctaRef = useRef<HTMLDivElement>(null);
  const ctaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showCta, setShowCta] = useState(false);
  const [ctaHeight, setCtaHeight] = useState(0);

  const [showRestartModal, setShowRestartModal] = useState(false);

  const videoUrl = useMemo(
    () => getCreationVideoUrl(event, person),
    [event, person],
  );

  const videoTransform = useMemo(() => {
    const translateX = `translateX(${videoOffset}px)`;
    const translateY =
      isMobileDevice && showCta
        ? `translateY(${ctaHeight + MOBILE_GAP}px)`
        : 'translateY(0)';
    return `${translateX} ${translateY}`;
  }, [videoOffset, isMobileDevice, showCta, ctaHeight]);

  const ctaTransform = useMemo(() => {
    return isMobileDevice
      ? `translateY(-${videoHeight + MOBILE_GAP}px)`
      : 'translateY(0)';
  }, [isMobileDevice, videoHeight]);

  // fetch data on mount
  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      await fetchHlReel();
      await fetchCreation(true);

      setIsLoading(false);
    };

    onMount();

    updateDemoData({ ...demoData, status: 'generation' });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.eventId]);

  // if video is generating – re-fetch every 10 seconds
  useEffect(() => {
    if (!isProcessing) return;

    const generationInterval = setInterval(async () => {
      await fetchCreation();
    }, 10000);

    return () => clearInterval(generationInterval);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing]);

  // preload video when URL is available
  useEffect(() => {
    if (!videoUrl || !videoRef.current) return;

    setIsVideoReady(false);
    videoRef.current.load();
  }, [videoUrl]);

  // measure heading height on mount
  useEffect(() => {
    if (headingRef.current) {
      setHeadingHeight(headingRef.current.offsetHeight);
    }
  }, []);

  // calculate video centering offset
  useEffect(() => {
    if (!containerRef.current) return;

    if (!showCta || isMobileDevice) {
      const containerWidth = containerRef.current.offsetWidth;
      const videoWidth = isStarted ? VIDEO_WIDTH_LARGE : VIDEO_WIDTH_SMALL;
      const offset =
        videoWidth >= containerWidth ? 0 : (containerWidth - videoWidth) / 2;
      setVideoOffset(offset);
    } else setVideoOffset(0);
  }, [isMobileDevice, isStarted, showCta]);

  // measure heights when it CTA appears
  useEffect(() => {
    if (showCta) {
      if (ctaRef.current) setCtaHeight(ctaRef.current.offsetHeight);
      if (videoRef.current) setVideoHeight(videoRef.current.offsetHeight);
    }
  }, [showCta]);

  // cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (ctaTimerRef.current) clearTimeout(ctaTimerRef.current);
    };
  }, []);

  const fetchCreation = async (didMount?: boolean) => {
    try {
      setIsProcessing(true);

      const person = await getCreationWithData(
        event.eventId,
        demoData.user.elementId,
      );

      if (person) {
        setPerson(person);
        setIsProcessing(false);

        if (!didMount) {
          trackDemoGenerationFinish();

          const freshDemoData = JSON.parse(
            localStorage.getItem('demoData') ?? '{}',
          );

          if (freshDemoData.notifications.generation)
            sendDemoStepCompleteEmail(demoData.user.email, 'Generation');
        }
      }
    } catch (err) {
      console.error(err);
      setError({ subheading: 'Error fetching some data' });
    }
  };

  const fetchHlReel = async () => {
    const { hlReel } = await getTemplateData(event.eventId, true);

    const wildcard = hlReel.shots.filter(
      (shot: HlReelShot) => shot.type === 'placeholder',
    )[0];

    setWildcardEnd(msecToSec(wildcard.segment.endTimestampMilliseconds));
  };

  const handleVideoCanPlay = () => {
    setIsVideoReady(true);
  };

  const handleVideoLoadedData = () => {
    setIsVideoReady(true);
  };

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    toast.error('Error loading video');
  };

  const handlePlay = () => {
    if (!videoRef.current || !isVideoReady) return;

    videoRef.current
      .play()
      .then(async () => {
        trackDemoGenerationPlayCreationSuccess();
        await sendSlackDemoNotification({
          action: 'Played Video',
          user: demoData.user,
        });
      })
      .catch(async (err) => {
        trackDemoGenerationPlayCreationError(err.message);
        await sendSlackDemoNotification({
          action: 'Played Video',
          user: demoData.user,
          details: err.message,
          isError: true,
        });
        console.error('Play error:', err);
        toast.error('Unable to play video. Please try again.');
      });

    setIsStarted(true);

    ctaTimerRef.current = setTimeout(
      () => setShowCta(true),
      (wildcardEnd + 2) * 1000,
    );
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(videoUrl);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'CrowdClip personalised video';
      a.click();
      window.URL.revokeObjectURL(url);

      trackDemoGenerationDownloadSuccess();
      toast.success("Downloaded to the device's memory");
    } catch (err: any) {
      console.error(err);
      trackDemoGenerationDownloadError(err.message);
    }
  };

  if (isLoading) return <PageSpinner noTips />;

  if (isProcessing)
    return (
      <DemoLoading
        type="generation"
        heading="Generating..."
        tipsList={generatingTipsList}
        expectedMin={10}
      />
    );

  return (
    <React.Fragment>
      <div
        ref={headingRef}
        className={classNames(
          'flex flex-col gap-4 items-center transition-all duration-500',
          isStarted ? 'opacity-0' : 'opacity-100',
        )}
      >
        <ContentHeader title="Your video is ready" />

        <div className="text-lg">
          This is what CrowdClip&reg; creates for every attendee
        </div>
      </div>

      <div
        ref={containerRef}
        className="w-full flex flex-col sm:flex-row gap-8 lg:gap-12 transition-all duration-1000"
        style={{
          transform: isStarted
            ? `translateY(-${headingHeight}px)`
            : 'translateY(0)',
        }}
      >
        {/* video */}
        <div
          className={classNames(
            'relative max-w-[90vw] rounded-md overflow-hidden transition-all duration-1000',
            isStarted ? 'w-80' : 'w-64',
          )}
          style={{ transform: videoTransform }}
        >
          {!isStarted && (
            <div
              onClick={handlePlay}
              className="group absolute w-full h-full flex items-center justify-center bg-black-overlay-400 text-white cursor-pointer z-10"
            >
              {!isVideoReady ? (
                <Spinner size="2xl" color="white" />
              ) : (
                <PlayCircle
                  size="4rem"
                  weight="fill"
                  className="transition-all group-hover:scale-110"
                />
              )}
            </div>
          )}

          <video
            ref={videoRef}
            className="w-full aspect-[9/16] object-contain bg-black"
            src={videoUrl}
            controls={isStarted}
            loop
            playsInline
            preload="auto"
            onCanPlay={handleVideoCanPlay}
            onLoadedData={handleVideoLoadedData}
            onError={handleVideoError}
          />
        </div>

        {/* CTA */}
        {showCta && (
          <div
            ref={ctaRef}
            className="flex-1 flex flex-col gap-4 items-center justify-center animate-fadeIn"
            style={{ transform: ctaTransform }}
          >
            <div className="text-[1.5rem] lg:text-[1.75rem]">
              You’ve just seen <b>one personalised reel</b>. Now imagine{' '}
              <b>every attendee</b> receiving their own version — automatically
              generated from your event footage.
            </div>

            <div className="text-[1.5rem] lg:text-[1.75rem]">
              Want to see this at event scale?
            </div>

            <div className="flex gap-6">
              <Button
                colour="black"
                onClick={() => {
                  window.open('https://www.crowdclip.com/contact', '_blank');
                  trackDemoGenerationClickContact();
                }}
              >
                Talk To Us
              </Button>
            </div>

            <div className="italic mt-4">
              Like your video? Feel free to{' '}
              <Button onClick={handleDownload} variant="link">
                download
              </Button>{' '}
              and share it. Tag @crowdclip so we can see it too.
            </div>

            <Button
              onClick={() => {
                setShowRestartModal(true);
                trackDemoResetClick();
              }}
              variant="link"
              className="text-sm italic"
            >
              <Repeat /> start demo again
            </Button>
          </div>
        )}
      </div>

      <RestartDemoModal
        show={showRestartModal}
        onHide={() => setShowRestartModal(false)}
      />
    </React.Fragment>
  );
};

export default GenerateStep;
